/**
 * 판의 **주요 장면**: 맞붙은 때, 장수 기술·동료 기술을 쓴 때, 적장이 쓰러진 때, 부대가 전멸한 때, 장수가 쓰러진 때.
 * 다시 보기(`ui/replay-view.ts`)의 시간 줄에 눈금으로 찍혀, 눌러서 그 장면으로 건너뛴다. 플레이한 사람이 골랐다 —
 * 지금까지는 8배로 훑거나 감으로 건너뛰어야 했다.
 *
 * 싸우는 동안 틱마다 판을 보며 적는다(`observe`). 다시 볼 때 기록을 처음부터 한 번 더 돌려 찾을 수도 있지만(결정론이라
 * 같은 장면이 나온다 — 시험이 못 박는다), 큰 판은 그 한 바퀴가 몇 초라 여는 데 걸린다. 싸울 때 적어 기록에 싣는다.
 *
 * 판을 읽기만 한다 — 시뮬레이션 상태를 쓰지 않는다. 그림을 모르는 셈이라 시험이 본다.
 */
import { fallTitle, fallenLeaders } from '../meta/foe-leaders.ts';
import { SKILLS, SKILL_CODE } from '../sim/skills.ts';
import { Team } from '../sim/units.ts';
import type { World } from '../sim/world.ts';

export type MomentKind = 'clash' | 'skill' | 'mate' | 'chief' | 'wiped' | 'leader';

export interface Moment {
  /** 그 일이 일어난 틱(그 틱을 돌린 뒤의 판에서 보인다). */
  readonly tick: number;
  readonly kind: MomentKind;
  /** 눈금의 풍선말과 알림에 적는 글. */
  readonly label: string;
}

/**
 * 한 판에 적는 장면의 상한. 기술은 재사용 대기가 있어 판마다 몇 번이지만, 저장한 기록을 손으로 고쳐 수천 개를 실어도
 * 눈금 수천 개를 그리지 않게 한다(읽을 때도 자른다, `meta/replays.ts`).
 */
export const MAX_MOMENTS = 60;

/** 부대 이름. `ui/labels.ts`의 `squadName`과 같은 글이다 — 화면 쪽을 끌어오지 않으려고 여기 한 줄 둔다(시험이 맞춘다). */
function squadLabel(sq: number): string {
  return sq === 0 ? '내 부대' : `제${sq + 1}부대`;
}

export class MomentTracker {
  readonly moments: Moment[] = [];
  private clashed = false;
  private roars: number;
  private readonly mates: number[];
  private chiefs: number;
  private leaderUp: boolean;
  /** 총대장이 살아 있었는가(지난 틱) — 부장이 쓰러진 것과 가른다. */
  private chiefUp: boolean;
  /** 적의 부대마다 대장이 살아 있었는가(지난 틱) — 쓰러진 사람의 이름을 적는 데 쓴다. */
  private readonly cmdUp: boolean[];
  /** 분대마다 살아 있는 사람이 있는가(비트). 지난 틱의 것. */
  private standing: number;
  private readonly skillName: string;
  private readonly raid: boolean;

  constructor(world: World, raid = false) {
    this.raid = raid;
    this.roars = world.roarCount;
    this.mates = Array.from(world.mateCount);
    this.chiefs = world.chiefsAlive();
    this.chiefUp = world.chiefAlive();
    this.cmdUp = Array.from(world.foeCmd, (c) => c >= 0 && world.alive[c] === 1);
    this.leaderUp = world.leaderAlive();
    this.standing = this.squadsStanding(world);
    this.skillName = (SKILLS.find((s) => SKILL_CODE[s.id] === world.skillCode) ?? SKILLS[0]).name;
  }

  /**
   * 분대마다 살아 있는 사람이 있는가(빠져나간 병사도 산 것이다 — 물러난 부대는 전멸이 아니다). 유닛을 한 번 훑어 비트로
   * 돌려준다 — 틱마다 도는 자리라 배열을 새로 만들지 않는다(리뷰 제안).
   */
  private squadsStanding(w: World): number {
    let up = 0;
    for (let i = 0; i < w.count; i++) {
      if (w.alive[i] === 1 && w.team[i] === Team.Player) up |= 1 << w.squad[i];
    }
    return up;
  }

  private add(w: World, kind: MomentKind, label: string): void {
    if (this.moments.length < MAX_MOMENTS) this.moments.push({ tick: w.tick, kind, label });
  }

  /** 한 틱을 돌린 뒤 부른다. 그 틱에 새로 생긴 장면을 적는다. */
  observe(w: World): void {
    if (!this.clashed && w.stats.damage[Team.Player] + w.stats.damage[Team.Enemy] > 0) {
      this.clashed = true;
      this.add(w, 'clash', '맞붙었다');
    }
    if (w.roarCount > this.roars) {
      this.roars = w.roarCount;
      this.add(w, 'skill', `장수 기술 — ${this.skillName}`);
    }
    for (let sq = 0; sq < this.mates.length; sq++) {
      if (w.mateCount[sq] === this.mates[sq]) continue;
      this.mates[sq] = w.mateCount[sq];
      this.add(w, 'mate', `${squadLabel(sq)}의 부하장수 기술`);
    }
    const chiefs = w.chiefsAlive();
    if (chiefs < this.chiefs) {
      this.chiefs = chiefs;
      const who = this.raid ? '두령' : '적장';
      // 총대장이 쓰러졌으면 그렇게, 부장이면 부장으로 적는다(적의 부대 — 적장 결전은 예전대로 "하나가").
      const lead = !w.chiefAlive() && this.chiefUp;
      this.chiefUp = w.chiefAlive();
      // 누가 쓰러졌는지 이름으로 적는다(`meta/foe-leaders.ts`). 한 틱에 여럿이면 함께 — 총대장과 부장은 직함을 가른다.
      const fell = fallenLeaders(w, this.cmdUp);
      const title = this.raid ? '' : fallTitle(fell.heads, fell.subs);
      const text =
        title ||
        (this.raid || w.warlords ? (chiefs > 0 ? `${who} 하나가 쓰러졌다` : `${who}이 쓰러졌다`) : lead ? '적장이 쓰러졌다' : '적의 부장이 쓰러졌다');
      this.add(w, 'chief', text);
    }
    const up = this.squadsStanding(w);
    const gone = this.standing & ~up;
    if (gone !== 0) {
      for (let sq = 0; sq < w.sqUsed.length; sq++) if (gone & (1 << sq)) this.add(w, 'wiped', `${squadLabel(sq)} 전멸`);
    }
    this.standing = up;
    if (this.leaderUp && !w.leaderAlive()) {
      this.leaderUp = false;
      this.add(w, 'leader', '장수가 쓰러졌다');
    }
  }
}

/** 장면 앞에서 이만큼(틱) 일찍 선다 — 그 일이 일어나는 것을 보려면 조금 앞에서 봐야 한다. */
export const MOMENT_LEAD = 45;

/**
 * 한 번에 띄우는 순간들의 결(`Hud.say`의 `tone`): 우리에게 나쁜 일(장수 전사·분대 전멸)이 하나라도 있으면 나쁜 일, 아니면
 * 좋은 일(적 대장 전사·기술)이 있으면 좋은 일, 그 밖(맞붙었다)은 그냥 알림이다.
 */
export function momentTone(kinds: readonly MomentKind[]): 'good' | 'bad' | 'info' {
  if (kinds.some((k) => k === 'leader' || k === 'wiped')) return 'bad';
  if (kinds.some((k) => k === 'chief' || k === 'skill' || k === 'mate')) return 'good';
  return 'info';
}

/** 지금 틱 다음의 장면. 없으면 null. 건너뛰면 `tick - MOMENT_LEAD`에 서므로, 거기 서 있을 때 그 장면을 다시 고르지 않는다. */
export function nextMoment(moments: readonly Moment[], tick: number): Moment | null {
  for (const m of moments) if (m.tick - MOMENT_LEAD > tick) return m;
  return null;
}

/**
 * 지금 틱 앞의 장면. 없으면 null. 방금 건너뛴 장면(지금이 그 앞 `MOMENT_LEAD` 안팎)은 건너 그 앞의 것을 고른다 —
 * 재생 중에는 틱이 흐르므로 `slack`만큼 여유를 둔다(안 두면 "이전"을 눌러도 같은 장면으로 되돌아왔다).
 */
export function prevMoment(moments: readonly Moment[], tick: number, slack = 30): Moment | null {
  let found: Moment | null = null;
  for (const m of moments) if (m.tick - MOMENT_LEAD < tick - slack) found = m;
  return found;
}

/** 시간 줄의 눈금 하나: 가까운 장면을 묶은 것. `tick`은 묶음의 첫 장면. */
export interface MomentMark {
  readonly tick: number;
  /** 묶음에서 가장 무거운 갈래 — 눈금의 색. */
  readonly kind: MomentKind;
  readonly moments: readonly Moment[];
}

/** 갈래의 무게. 묶음의 색은 가장 무거운 것으로 정한다 — 전멸·적장 곁의 기술 눈금이 그것을 가리지 않게. */
const WEIGHT: Record<MomentKind, number> = { leader: 5, chief: 4, wiped: 3, clash: 2, skill: 1, mate: 1 };

/**
 * 가까운 장면을 한 눈금으로 묶는다. `gap`(틱) 안에 이어지는 장면은 같은 눈금이다(묶음의 첫 장면에서 잰다 — 사슬처럼
 * 끝없이 이어 붙지 않게).
 *
 * 처음에는 장면마다 눈금을 찍었다. 맞붙은 직후의 기술 둘, 판 끝의 전멸 연쇄처럼 일이 몰리면 눈금이 포개져(폰 360에서
 * 3px·0px 간격) 누르면 이웃 눈금이 받았고, 같은 틱의 두 장면은 하나만 눌렸다(QA가 쟀다).
 */
export function groupMoments(moments: readonly Moment[], gap: number): MomentMark[] {
  const out: { tick: number; kind: MomentKind; moments: Moment[] }[] = [];
  for (const m of moments) {
    const last = out[out.length - 1];
    if (last && m.tick - last.tick <= gap) {
      last.moments.push(m);
      if (WEIGHT[m.kind] > WEIGHT[last.kind]) last.kind = m.kind;
    } else {
      out.push({ tick: m.tick, kind: m.kind, moments: [m] });
    }
  }
  return out;
}

/** 알림 하나에 모아 적는 장면의 수. */
const RECENT_MAX = 4;

/**
 * 방금 지난 장면들: `tick`에서 `window`틱 안에 지난 것, 오랜 것부터(많으면 뒤의 `RECENT_MAX`개). 알림이 한꺼번에 적는다.
 *
 * 처음에는 지난 장면 하나만 알렸다. 일이 몰리면 앞의 알림이 곧바로 덮여 — 2틱 뒤의 다음 장면에 밀려 0.05초만 보였다
 * (QA가 쟀다). 싸울 때의 알림이 같은 틱의 여럿을 한 알림에 줄마다 적는 것과 같게 한다.
 */
export function recentMoments(moments: readonly Moment[], tick: number, window: number, floor = -1): Moment[] {
  // `floor`: 이 틱까지는 건너뛴 구간이다(마지막으로 건너뛰어 선 틱) — 그 안의 장면은 지나는 것을 보지 못했으니 적지
  // 않는다. 처음에는 창만 보아, 장면 앞으로 건너뛴 뒤 재생하면 건너뛴 장면이 다음 알림에 섞여 나왔다(재리뷰가 재현했다).
  const out = moments.filter((m) => m.tick <= tick && m.tick > tick - window && m.tick > floor);
  return out.slice(-RECENT_MAX);
}
